import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import ReactGridLayout from "react-grid-layout";
import { generateRandomSentence } from "./MoreDetailsOperations";
import BidOfferTable from "./comp/BidOfferTable";
import PriceRange from "./comp/PriceRange";
import VolumeDetails from "./comp/VolumeDetails";
import "../../css/MoreDetails.css";
import { useTickContext } from "@dashboard/contexts/TickContext";
import { useInstrumentRowContext } from "@dashboard/contexts/InstrumentRowContext";
import { useRowSelectionContext } from "@portfolio/context/RowSelectionContextProvider";
import { setGridItemHeight } from "@dashboard/grid-components/announcements/AnnouncementUtil";

const MAX_HEIGHT_DETAILS = 520; // same cap style as AnnouncementList

const MoreDetailsLayout = (props) => {
    const [rowHeight, setRowHeight] = useState(40); // (not really used, but kept)
    const { setLayouts: propSetLayouts, instrumentToken, layoutIndex, rowWrapperRef } = props;

    const tickObject = useTickContext();
    const tick = tickObject.tick;

    const moreDetailsRef = useRef(null);
    const bidOfferTableRef = useRef(null);

    const [bids, setBids] = useState([]);
    const [offers, setOffers] = useState([]);
    const [open, setOpen] = useState(0);
    const [low, setLow] = useState(0);
    const [high, setHigh] = useState(0);
    const [close, setClose] = useState(0);
    const [lastPrice, setLastPrice] = useState(0);
    const [volume, setVolume] = useState(0);
    const [avgPrice, setAvgPrice] = useState(0);
    const [lowerCircuit, setLowerCircuit] = useState(0);
    const [upperCircuit, setUpperCircuit] = useState(0);
    const [ltq, setLtq] = useState(0);
    const [ltt, setLtt] = useState("N/A");
    const defaultLevels = Array(5).fill({ price: 0, orders: 0, quantity: 0 });

    // from InstrumentRowContext
    const {
        rowVirtualizer,
        tabID,
        GUIInstrument,
        index,
        listRef,
        setLayouts: ctxSetLayouts,
    } = useInstrumentRowContext();

    // from RowSelectionContext (same thing AnnouncementList uses)
    const { rowHeightGridLayout } = useRowSelectionContext();

    // use whichever setLayouts is available (context preferred)
    const setLayouts = ctxSetLayouts || propSetLayouts;

    const lastHeightUnitsRef = useRef(null);
    const lastScrollHeightRef = useRef(null);

    // 🔁 EXACT same "measure → setGridItemHeight → measureElement" pattern as AnnouncementList
    useLayoutEffect(() => {
        if (!moreDetailsRef.current || !rowVirtualizer || !rowHeightGridLayout || !setLayouts) return;

        const container = moreDetailsRef.current;
        const fullHeight = container.scrollHeight || 0;
        const totalHeight = Math.min(fullHeight, MAX_HEIGHT_DETAILS);
        const heightInGridUnits = totalHeight / rowHeightGridLayout;

        const prevHeight =
            typeof lastHeightUnitsRef.current === "number"
                ? lastHeightUnitsRef.current
                : 0;
        const prevScroll =
            typeof lastScrollHeightRef.current === "number"
                ? lastScrollHeightRef.current
                : 0;

        // avoid loops if nothing really changed
        if (
            fullHeight === 0 ||
            (Math.abs(prevHeight - heightInGridUnits) < 0.1 &&
                fullHeight === prevScroll)
        ) {
            return;
        }

        lastHeightUnitsRef.current = heightInGridUnits;
        lastScrollHeightRef.current = fullHeight;

        // 1) update the layout.h for this row
        setGridItemHeight(heightInGridUnits, setLayouts, index);

        // 2) ask the virtualizer to re-measure this row's DOM node
        requestAnimationFrame(() => {
            const domNode =
                listRef?.current?.querySelector(
                    `[data-row-key='${tabID}-${GUIInstrument.instrumentToken}']`
                );
            if (domNode) {
                rowVirtualizer?.measureElement(domNode);
            }
        });
    }, [tick, rowHeightGridLayout, setLayouts, rowVirtualizer, tabID, index, listRef, GUIInstrument?.instrumentToken]);

    useEffect(() => {
        if (tick) {
            setBids(
                (tick.depth?.buy?.map((bid) => ({
                    price: bid.price,
                    orders: bid.orders,
                    quantity: bid.quantity,
                })) || defaultLevels).slice(0, 5)
            );

            setOffers(
                (tick.depth?.sell?.map((offer) => ({
                    price: offer.price,
                    orders: offer.orders,
                    quantity: offer.quantity,
                })) || defaultLevels).slice(0, 5)
            );

            setOpen(tick.openPrice || 0);
            setLow(tick.lowPrice || 0);
            setHigh(tick.highPrice || 0);
            setClose(tick.closePrice || 0);
            setLastPrice(tick.lastTradedPrice || 0);
            setVolume(tick.volumeTradedToday || 0);
            setAvgPrice(tick.averageTradePrice || 0);
            setLowerCircuit(tick.lowPrice || 0);
            setUpperCircuit(tick.highPrice || 0);
            setLtq(tick.totalBuyQuantity || 0);
            setLtt(tick.lastTradedTime || "N/A");
        }
    }, [tick]);

    return (
        <div className="tick-data-display pf-tick-data-display" ref={moreDetailsRef}>
            <BidOfferTable bids={bids} offers={offers} ref={bidOfferTableRef} />

            <div className="ohlc-section1">
                <div className="ohlc-section2">
                    <div className="ohlc">
                        <div className="ohlc-pair">
                            <div className="label">Open</div>
                            <div className="value">{open}</div>
                        </div>
                        <div className="ohlc-pair">
                            <div className="label">Low</div>
                            <div className="value">{low}</div>
                        </div>
                    </div>
                    <div className="ohlc">
                        <div className="ohlc-pair">
                            <div className="label">Prev. Close</div>
                            <div className="value">{close}</div>
                        </div>
                        <div className="ohlc-pair">
                            <div className="label">High</div>
                            <div className="value">{high}</div>
                        </div>
                    </div>
                </div>
            </div>

            <VolumeDetails
                volume={volume}
                avgPrice={avgPrice}
                lowerCircuit={lowerCircuit}
                upperCircuit={upperCircuit}
                ltq={ltq}
                ltt={ltt}
            />

            <div className="amount-cr-parent">
                <div className="amount-cr">
                    <div className="amount-label">Amount Traded in crores</div>
                    <div className="amount-value">
                        {avgPrice != null && volume != null
                            ? ((Number(avgPrice) * Number(volume)) / 10000000).toFixed(
                                2
                            )
                            : "N/A"}
                        <span> Cr</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MoreDetailsLayout;
